import React, { useState, useEffect } from "react";

const SpecialsContent = ({ special, dayOfWeek, isLoading, error, onOrderClick }) => {
  const [currentSpecialIndex, setCurrentSpecialIndex] = useState(0);

  // Reset to first special when new data loads
  useEffect(() => {
    setCurrentSpecialIndex(0);
  }, [special]);

  if (isLoading) {
    return (
      <div className="hero is-fullheight is-flex is-align-items-center is-justify-content-center">
        <div className="has-text-centered">
          <p className="colorWhite">...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hero is-fullheight is-flex is-align-items-center is-justify-content-center" 
           style={{
             background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
             color: 'white'
           }}>
        <div className="has-text-centered">
          <h1 className="h1 colorWhite is-size-1">Oops!</h1>
          <p className="subtitle colorWhite">{error}</p>
          <p className="colorWhite">Please check back later or contact us directly.</p>
        </div>
      </div>
    );
  }

  if (!special || !special.specials || special.specials.length === 0) {
    return (
      <div className="hero is-fullheight is-flex is-align-items-center is-justify-content-center"
           style={{
             background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
             color: 'white'
           }}>
        <div className="has-text-centered">
          <h1 className="title colorWhite is-size-1">No Specials Today</h1>
          <p className="subtitle colorWhite">Check back tomorrow for our daily special!</p>
        </div>
      </div>
    );
  }

  const currentSpecial = special.specials[currentSpecialIndex];
  const hasMultipleSpecials = special.specials.length > 1;

  const handleNextSpecial = () => {
    setCurrentSpecialIndex((prev) => (prev + 1) % special.specials.length);
  };

  const handlePrevSpecial = () => {
    setCurrentSpecialIndex((prev) => (prev - 1 + special.specials.length) % special.specials.length);
  };

  const handleOrderClick = () => {
    if (onOrderClick) {
      onOrderClick(currentSpecial, special.dayOfWeek);
    } else {
      // Default behavior - could link to ordering system
      
    }
  };

  return (
    <div 
      className="hero is-fullheight"
      style={{
        backgroundImage: currentSpecial.heroImageSrc 
          ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${currentSpecial.heroImageSrc})`
          : `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        position: 'relative'
      }}
    >
      {/* REMOVED: Navigation arrows from sides */}
      {/* REMOVED: Special indicators (1, 2 buttons) from top right */}
      {/* 
      Commented out the numbered indicators that were in the top right:
      
      {hasMultipleSpecials && (
        <div 
          className="special-indicators"
          style={{
            position: 'absolute',
            top: '2rem',
            right: '2rem',
            zIndex: 10,
            display: 'flex',
            gap: '0.5rem'
          }}
        >
          {special.specials.map((_, index) => (
            <button
              key={index}
              className={`button is-small is-rounded ${index === currentSpecialIndex ? 'is-primary' : 'is-white'}`}
              onClick={() => setCurrentSpecialIndex(index)}
              style={{
                width: '2rem',
                height: '2rem',
                padding: 0,
                fontSize: '0.75rem'
              }}
            >
              {index + 1}
            </button>
          ))}
        </div>
      )}
      */}

      {/* Main content */}
      <div className="hero-body">
        <div className="container has-text-centered">
          <div className="columns is-centered">
            <div className="column is-8-tablet is-6-desktop">
              
              {/* Day and "Today's Special" header */}
              <div className="content-space-50-mobile mb-6">
                <p className="big colorWhite is-size-4 is-size-3-tablet mb-2" 
                   style={{ fontWeight: '400', letterSpacing: '0.1em' }}>
                  {special.isToday && special.dayOfWeek === 'Thursday' ? "Today's Specials" :
                   special.isToday ? "Today's Special" : 
                   special.dayOfWeek === 'Thursday' ? `${special.dayOfWeek}'s Specials` :
                   `${special.dayOfWeek}'s Special`}
                </p>
                <div 
                  style={{
                    width: '4rem',
                    height: '1px',
                    backgroundColor: 'white',
                    margin: '0 auto'
                  }}
                ></div>
              </div>

              {/* Special name */}
              <h1 
                className="mb-5 specials colorWhite " 
              >
                {currentSpecial.name}
              </h1>

              {/* Description */}
              <p 
                className="subtitle colorWhite is-size-5 is-size-4-tablet mb-6"
                style={{ 
                  fontWeight: '400',
                  lineHeight: '1.6',
                  maxWidth: '600px',
                  margin: '0 auto 3rem auto'
                }}
              >
                {currentSpecial.description}
              </p>

              {/* Order button with navigation arrows on sides */}
              <div className="is-flex is-align-items-center is-justify-content-center" style={{ gap: '1rem' }}>
                {/* Left arrow - only show if multiple specials */}
                {hasMultipleSpecials && (
                  <button 
                    className="button is-white is-rounded special-nav-btn"
                    onClick={handlePrevSpecial}
                    style={{
                      width: '3rem',
                      height: '3rem',
                      fontSize: '1.5rem'
                    }}
                  >
                    ←
                  </button>
                )}

                {/* Order button */}
                <button 
                  className="button locationOrdering px-6 py-4"
                  onClick={handleOrderClick}
                >
                  {currentSpecial.label || 'Order'}
                </button>

                {/* Right arrow - only show if multiple specials */}
                {hasMultipleSpecials && (
                  <button 
                    className="button is-white is-rounded special-nav-btn"
                    onClick={handleNextSpecial}
                    style={{
                      width: '3rem',
                      height: '3rem',
                      fontSize: '1.5rem'
                    }}
                  >
                    →
                  </button>
                )}
              </div>

              {/* Optional: Show current special indicator below order button */}
              {hasMultipleSpecials && (
                <div className="mt-4">
                  <p className="colorWhite is-size-7" style={{ opacity: 0.8 }}>
                    {currentSpecialIndex + 1} of {special.specials.length}
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div 
        className="has-text-centered"
        style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10
        }}
      >
        <div 
          style={{
            width: '2px',
            height: '3rem',
            backgroundColor: 'white',
            margin: '0 auto',
            opacity: 0.6,
            animation: 'pulse 2s infinite'
          }}
        ></div>
        <div 
          style={{
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '10px solid white',
            margin: '0 auto',
            opacity: 0.6
          }}
        ></div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.3; }
        }
        
        .special-nav-btn:hover {
          transform: scale(1.1);
        }
        
      `}</style>
    </div>
  );
};

export default SpecialsContent;